import { ClientPageType, DeliveryClientPageType, ROUTES } from '@/constants'
import { dateHelper } from '@/helpers/dateHelper'
import { AVAILABLE_LANGUAGES, i18n } from '@/plugins/i18n'
import DeliveryService from '@/services/delivery/DeliveryService'
import { SET_IS_LOADING_APP } from '@/store/mutation-types'
import { SnackBarType } from '@/store/types'
import { ClientPage, ContactType, Delivery, HeaderContent, MultiTrackingGlobalOrder } from '@/types'
import { HTTP_403_FORBIDDEN } from '@meltingpoint/shared-objects'
import Vue from 'vue'

export default Vue.extend({
  data: () => ({
    clientPage: null as ClientPage | null,
    delivery: null as Delivery | null,
    deliveries: [] as Delivery[],
    globalOrder: null as MultiTrackingGlobalOrder | null,
    availableLanguages: [] as string[],
    languages: [] as string[],
    pages: [] as ClientPage[],
    headerData: null as HeaderContent | null,
    minDeliveryPromiseDate: null as string | null,
  }),
  methods: {
    setLanguage(language: string, languages: string[]) {
      setTimeout(() => {
        this.availableLanguages = languages!
        this.$root.$i18n.i18next.language = language
        i18n.changeLanguage(language)
        dateHelper.changeLocale(language)
        // This force update of i18n system to allow the use of this.$t in methods and computeds
        this.$root.$i18n.i18nLoadedAt = dateHelper.getDate().toString()
      }, 250)
      this.$store.commit(SET_IS_LOADING_APP, false)
    },

    async getClientPage(language: string = navigator.language) {
      try {
        const {
          name: type,
          params: { accessToken },
        } = this.$route

        if (!accessToken || !type) {
          throw new Error(`Can't access to route params`)
        }
        let pageType = type
        const isAppointment = [
          DeliveryClientPageType.APPOINTMENT,
          DeliveryClientPageType.APPOINTMENT_CONFIRM,
        ].includes(type as DeliveryClientPageType)
        if (isAppointment) {
          pageType = DeliveryClientPageType.APPOINTMENT
        }
        const params: Record<string, any> = {
          type: pageType,
          language: AVAILABLE_LANGUAGES.includes(language)
            ? language
            : navigator.language?.split('-')[0],
        }
        const {
          delivery,
          deliveries,
          pages,
          globalOrder,
          languages = null,
          minDeliveryPromiseDate,
        } = await DeliveryService.get(accessToken, params)

        if (!deliveries || !deliveries.length || !pages.length) {
          throw new Error('No delivery found for given access token or no client page configured')
        }

        if (delivery) {
          this.delivery = delivery
        }

        if (deliveries) {
          this.delivery = deliveries[0]
          this.deliveries = deliveries
        }

        if (globalOrder && deliveries) {
          this.globalOrder = { ...deliveries[0], ...globalOrder } as any
        }

        if (languages) {
          this.languages = languages
        }

        if (pages) {
          this.pages = pages
        }

        if (minDeliveryPromiseDate) {
          this.minDeliveryPromiseDate = minDeliveryPromiseDate
        }

        if (this.globalOrder || this.delivery) {
          const obj: any =
            this.$route.name === ClientPageType.DELIVERY.MULTI_TRACKING
              ? this.globalOrder
              : this.delivery

          const { retailer, carrier, lockerCarrier, order, deliveryDate, pickingDate } = obj

          this.headerData = {
            retailerLogo: retailer.logo,
            ...(isAppointment
              ? {
                  carrierLogo: null,
                  lockerLogo: null,
                  lockerName: null,
                  carrierName: null,
                }
              : {
                  carrierLogo: carrier.logo,
                  lockerLogo: lockerCarrier?.logo,
                  lockerName: lockerCarrier?.name,
                  carrierName: carrier.name,
                }),
            referenceNumber: order.referenceNumber,
            createdAt: order.createdAt,
            timezone: obj.delivery.contact.timezone,
            deliveryDate,
            pickingDate,
          }
        }

        this.clientPage = pages[0]

        language && languages!.includes(language)
          ? this.setLanguage(language, languages!)
          : this.setLanguage(this.clientPage!.language, languages!)
      } catch (e) {
        if (e.response.status === HTTP_403_FORBIDDEN) {
          const isAppointment = this.$route.name === DeliveryClientPageType.APPOINTMENT
          const message = isAppointment
            ? this.$t('messages.appointment.noModificationPermission')
            : this.$t('messages.deliveryOutdated')
          this.$store.dispatch('setSnackbar', {
            message,
            type: isAppointment ? SnackBarType.WARNING : SnackBarType.ERROR,
            timeout: 10000,
          })
          return
        }
        this.$router.push({ name: ROUTES.NOT_FOUND })
      }
    },
  },
  computed: {
    showCarrierName(): boolean {
      if (this.clientPage?.config.carrierInfos?.showCarrierName === undefined) {
        return true
      }
      return !!this.clientPage?.config.carrierInfos?.showCarrierName
    },
    showCarrierLogo(): boolean {
      return !!this.clientPage?.config.carrierInfos?.showCarrierLogo
    },
    showClaimUrl(): boolean {
      return !!this.clientPage?.config.carrierInfos?.showClaimUrl
    },
    showCarrierEmail(): boolean {
      if (this.clientPage?.config.carrierInfos) {
        if (this.clientPage?.config.carrierInfos.email.type === ContactType.CARRIER) {
          return this.delivery?.carrier.contact?.email !== undefined
        }
        return this.clientPage?.config.carrierInfos.email.type !== ContactType.EMPTY
      }
      return false
    },
    showCarrierPhone(): boolean {
      if (this.clientPage?.config.carrierInfos) {
        if (this.clientPage?.config.carrierInfos?.phone.type === ContactType.CARRIER) {
          return this.delivery?.carrier.contact?.phone !== undefined
        }
        return this.clientPage?.config.carrierInfos?.phone.type !== ContactType.EMPTY
      }
      return false
    },
    carrierEmail(): string | undefined {
      if (this.clientPage?.config.carrierInfos?.email.type === ContactType.CUSTOM) {
        return this.clientPage?.config.carrierInfos?.email.value
      }
      return this.delivery?.carrier.contact?.email
    },
    carrierPhone(): string | undefined {
      if (this.clientPage?.config.carrierInfos?.phone.type === ContactType.CUSTOM) {
        return this.clientPage?.config.carrierInfos?.phone.number
      }
      return this.delivery?.carrier.contact?.phone
    },
    showCarrierInfosCard(): boolean {
      return (
        this.delivery !== null &&
        (this.showCarrierName ||
          this.showCarrierLogo ||
          this.showClaimUrl ||
          this.showCarrierEmail ||
          this.showCarrierPhone)
      )
    },
    showLockerLogo(): boolean {
      return !!this.clientPage?.config.lockerInfos?.showLockerLogo
    },
    showLockerName(): boolean {
      return !!this.clientPage?.config.lockerInfos?.showLockerName
    },
    showLockerEmail(): boolean {
      if (this.clientPage?.config.lockerInfos) {
        if (this.clientPage?.config.lockerInfos.email.type === ContactType.CUSTOM) {
          return this.delivery?.carrier.contact?.email !== undefined
        }
        return this.clientPage?.config.lockerInfos.email.type !== ContactType.EMPTY
      }
      return false
    },
    showLockerPhone(): boolean {
      if (this.clientPage?.config.lockerInfos) {
        if (this.clientPage?.config.lockerInfos?.phone.type === ContactType.CUSTOM) {
          return this.delivery?.carrier.contact?.phone !== undefined
        }
        return this.clientPage?.config.lockerInfos?.phone.type !== ContactType.EMPTY
      }
      return false
    },
    lockerEmail(): string | undefined {
      if (this.clientPage?.config.lockerInfos?.email.type === ContactType.CUSTOM) {
        return this.clientPage?.config.lockerInfos?.email.value
      }
    },
    lockerPhone(): string | undefined {
      if (this.clientPage?.config.lockerInfos?.phone.type === ContactType.CUSTOM) {
        return this.clientPage?.config.lockerInfos?.phone.number
      }
    },
    showLockerInfosCard(): boolean {
      return (
        this.delivery?.lockerCarrier !== undefined &&
        (this.showLockerName || this.showLockerLogo || this.showLockerEmail || this.showLockerPhone)
      )
    },
  },
})
